<template>
  <VForm autocomplete="off" @submit.prevent>
    <div v-if="inviteLink">
      <CandidateInterviewCopyLink v-if="inviteLink" :link="inviteLink">
        <b class="d-block mb-1">{{ $t('sendCandidateLink') }}</b>
        <div class="mb-10 mx-auto" style="max-width: 30rem">
          {{ $t('sendCandidateLinkText') }}
        </div>
        <VBtn
          v-if="candidatePhone && notifyBy === notificationTypes.EMAIL_AND_WHATSAPP"
          @click="openWhatsApp(whatsappInviteText)"
          color="success"
          x-large
          class="mb-3"
        >
          <img src="../assets/img/candidate/log/icons/whatsApp.svg" alt="" class="mr-2">
          {{ $t('sendWhatsappInviteButton') }}
        </VBtn>
      </CandidateInterviewCopyLink>
    </div>
    <div v-else>
      <div class="text-center">
        <VCard
          tile
          outlined
          class="mb-6"
          v-if="invitationType.OFFICE_INTERVIEW || showExtensionTip"
        >
          <VSheet
            class="pa-3 body-2 font-weight-medium"
            color="yellow lighten-4"
            tile
          >
            <template v-if="invitationType.OFFICE_INTERVIEW">
              {{ $t('addCandidateTip') }}
            </template>
            <i18n v-else-if="showExtensionTip" path="chromeExt1" tag="label">
              <a :href="chromeExtensionURL" target="_blank">
                {{ $t('chromeExt2') }}
              </a>
            </i18n>
          </VSheet>
        </VCard>
      </div>
      <VRow>
        <VCol cols="12" md="6" class="py-0 mt-1 mb-1">
          <VTextField
            id="firstName"
            v-model="firstName"
            autofocus
            outlined
            dense
            autocomplete="given-name"
            :error-messages="errors.firstName"
            @input="clearError('firstName')"
          >
            <template v-slot:label>
              {{ $t('firstName') }} *
            </template>
          </VTextField>
        </VCol>
        <VCol cols="12" md="6" class="py-0 mt-1 mb-1">
          <VTextField
            id="lastName"
            v-model="lastName"
            outlined
            dense
            autocomplete="family-name"
            :error-messages="errors.lastName"
            @input="clearError('lastName')"
          >
            <template v-slot:label>
              {{ $t('lastName') }} *
            </template>
          </VTextField>
        </VCol>
      </VRow>
      <VRow>
        <VCol cols="12" md="6" class="py-0 mt-1 mb-1">
          <VTextField
            id="email"
            v-model="email"
            type="email"
            outlined
            dense
            label="E-mail"
            autocomplete="disable-autocomplete"
            :error-messages="errors.email"
            @input="clearError('email')"
          >
            <template v-slot:label>
              E-mail *
            </template>
          </VTextField>
        </VCol>
        <VCol cols="12" md="6" class="py-0 mt-1 mb-1">
          <PhoneNumberInput
            id="phone"
            :key="country"
            v-model="phone"
            :label="$t('phone')"
            :default-country="country"
            :auto-default-country="false"
            :error-messages="errors.phone"
            @input="clearError('phone')"
          />
        </VCol>
      </VRow>
      <VRow>
        <VCol
          v-for="(addField, index) in vacancyCandidateAdditionalFields"
          :key="index"
          cols="12"
          :sm="$route.meta.iframe ? 6 : 12"
          class="py-0 mt-1 mb-1"
          md="6"
        >
          <CandidateCreateFormAdditionalInput
            :add-field="addField"
            :error="errors"
            @setAdditionalValue="setAdditionalValue"
            @clearError="clearError(`additionalFields.${addField.value}`)"
          />
        </VCol>
      </VRow>
      <VTextField
        id="resume"
        v-model="resumeName"
        type="resume"
        outlined
        dense
        readonly
        :label="$t('resume')"
        :rules="resumeRules"
        clearable
        prepend-inner-icon="mdi-file-outline"
        @click="addFile"
        @click:prepend-inner="addFile"
        @click:clear="clearResume"
      >
        <template v-slot:label>
          {{ $t('resume') }}
        </template>
      </VTextField>
      <template v-show="false">
        <input
          v-show="false"
          ref="resumeInput"
          type="file"
          :accept="resumeAvailableTypes"
          @change="handleFileUpload( $event )"
        >
      </template>

      <VCheckbox
        v-if="confirmationText"
        v-model="confirmed"
        class="mt-n2"
        :label="confirmationText"
        type="checkbox"
      />

      <VCheckbox
        v-if="!isOfficeInterview"
        v-model="withInvitation"
        class="mt-n2"
        :label="$t('inviteToVirtualInterview')"
        type="checkbox"
      />

      <template v-if="isNotificationAvailable && withInvitation">
        <QuotaResolver v-slot:default="{quota}" class="notify">
          <VRadioGroup v-model="notifyBy">
            <VRadio
              class="notify__item"
              :value="notificationTypes.EMAIL"
            >
              <template #label>
                {{ $t('notifyByEmail') }}
              </template>
            </VRadio>
            <VRadio
              class="notify__item"
              v-for="notifyType in notifyTypes"
              :key="notifyType.value"
              :value="notifyType.value"
            >
              <template #label>
                {{ notifyType.text }}
                <span class="notify__rest-amount">
                  ({{ quota[notifyType.quotaCode] }})<span class="notify__rest-hint"> {{ $tc(`${notifyType.value}Tooltip`, quota[notifyType.quotaCode]) }} </span>
                </span>
              </template>
            </VRadio>
          </VRadioGroup>
        </QuotaResolver>
      </template>

      <VCheckbox
        v-if="confirmationText"
        v-model="confirmed"
        class="mt-n4"
        :label="confirmationText"
        type="checkbox"
      />

      <VAlert
        v-if="errors.notifyBySms"
        type="error"
      >
        {{ errors.notifyBySms.toString() }}
      </VAlert>

      <VAlert
        v-else-if="errors.notifyByCall"
        type="error"
      >
        {{ errors.notifyByCall.toString() }}
      </VAlert>

      <VAlert
        v-else-if="errorMessage"
        type="error"
      >
        {{ errorMessage }}
      </VAlert>

      <div class="d-sm-flex align-center flex-wrap" style="width: 100%">
        <VBtn
          class="mr-4"
          id="inviteBtn"
          color="success"
          :disabled="!confirmed"
          :loading="loading"
          @click="submit"
        >
          {{ $t('invite') }}
        </VBtn>
        <VBtn
          color="success"
          outlined
          @click="$emit('cancel')"
        >
          {{ $t('cancel') }}
        </VBtn>
      </div>
    </div>
  </VForm>
</template>

<script>
import CandidateInterviewCopyLink from '@/components/Candidate/CandidateInterviewCopyLink';
import { useI18n } from 'vue-i18n-composable';
import QuotaResolver from '@/components/Payment/Quota/QuotaResolver';
import { computed } from '@vue/composition-api';
import Company from '@/orm/models/Company/Company';
import {
  invitationType, candidateNotificationTypes, quotaItemTypes, localeType,
} from '@/enums';
import { normalizePhoneNumber, objectToFormData } from '@/functions';
import { response } from '@/orm/functions';
import { mdiFileOutline } from '@mdi/js';
import Resume from '@/orm/models/Candidate/Resume';
import CandidateCreateFormAdditionalInput from '@/components/Input/CandidateCreateFormAdditionalInput.vue';
import ValidationMixin from '../mixins/ValidationMixin';
import RecaptchaMixin from '../mixins/RecaptchaMixin';
import PhoneNumberInput from '../components/Input/PhoneNumberInput.vue';
import Candidate from '../orm/models/Candidate/Candidate';

export default {
  name: 'CandidateCreateForm',
  components: {
    CandidateCreateFormAdditionalInput, QuotaResolver, CandidateInterviewCopyLink, PhoneNumberInput,
  },
  mixins: [ValidationMixin, RecaptchaMixin],
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    invitationType: {
      type: String,
      required: true,
      validator(value) {
        return Object.values(invitationType).indexOf(value) !== -1;
      },
    },
    confirmationText: {
      type: String,
      default: '',
    },
    isOfficeInterview: {
      required: true,
      type: Boolean,
    },
  },
  setup() {
    const { t } = useI18n();
    const company = computed(() => {
      return Company.query().with('country').first();
    });

    const country = computed(() => {
      return company.value?.country?.alpha2Code;
    });

    const notifyTypes = [
      { value: candidateNotificationTypes.SMS, text: t('notifyBySMS'), quotaCode: quotaItemTypes.SMS },
      { value: candidateNotificationTypes.CALL, text: t('notifyByCall'), quotaCode: quotaItemTypes.CALLS },
    ];

    return {
      notifyTypes,
      country,
      company,
    };
  },
  data() {
    return {
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      confirmed: this.confirmationText === '',
      notifyByEmail: true,
      notifyBy: candidateNotificationTypes.EMAIL,
      chromeExtensionURL: process.env.VUE_APP_CHROME_EXTENSION_URL,
      inviteLink: null,
      withInvitation: true,
      resume: null,
      mdiFileOutline,
      resumeAvailableTypes: '.pdf,.doc,.docx,.odt,.rtf',
      resumeRules: [
        () => !this.resumeError || this.resumeError,
      ],
      resumeName: null,
      resumeError: null,
      candidatePhone: null,
      notificationTypes: candidateNotificationTypes,
      additionalFields: [],
    };
  },
  computed: {
    isNotificationAvailable() {
      return this.invitationType === invitationType.ONLINE_INTERVIEW;
    },
    showExtensionTip() {
      return this.$i18n.locale !== localeType.EN;
    },
    whatsappInviteText() {
      return this.$t('whatsAppInviteMessage', { companyName: this.company?.name, url: this.inviteLink });
    },
    whatsAppUrl() {
      return `https://api.whatsapp.com/send/?phone=${this.getClearPhone(this.candidatePhone)}`;
    },
    whatsAppApplicationUrl() {
      return `whatsapp://send?phone=${this.getClearPhone(this.candidatePhone)}&text=${this.whatsappInviteText}`;
    },
    vacancyCandidateAdditionalFields() {
      return this.vacancy.candidateAdditionalFields;
    },
  },
  created() {
    if (this.isOfficeInterview) {
      this.withInvitation = false;
    }

    this.candidatePhone = null;
  },
  mounted() {
    this.setAdditionalInputs();
  },
  methods: {
    async submit() {
      await this.withValidationHandling(async () => {
        const recaptchaToken = await this.getRecaptchaToken('candidateCreate');
        const result = this.withInvitation
          ? await this.storeWithInvitation(recaptchaToken)
          : await this.storeWithoutInvitation(recaptchaToken);
        if (!result) {
          return;
        }
        const model = await Candidate.find(result.id);
        await this.storeResume(model);
        if ((model && this.withInvitation) || this.isOfficeInterview) {
          this.inviteLink = model.inviteLink;
          window.candidateId = model.id;
          this.candidatePhone = model.phone;
        }
        this.$emit('created', model);
        if (!this.withInvitation && !this.isOfficeInterview) {
          this.$emit('cancel');
        }
        return model;
      });
    },
    async storeWithInvitation(recaptchaToken) {
      return response(Candidate.api().store(this.vacancy.id, {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phone: normalizePhoneNumber(this.phone),
        additionalFields: this.additionalFields,
        invitationType: this.invitationType,
        notifyBySms: this.notifyBy === candidateNotificationTypes.SMS,
        notifyByCall: this.notifyBy === candidateNotificationTypes.CALL,
        recaptchaToken,
      }));
    },
    async storeWithoutInvitation(recaptchaToken) {
      return response(Candidate.api().storeWithoutInvitation(this.vacancy.id, {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phone: normalizePhoneNumber(this.phone),
        additionalFields: this.additionalFields,
        invitationType: this.invitationType,
        recaptchaToken,
      }));
    },
    async storeResume(candidate) {
      if (this.resume) {
        const { resume } = this;
        Resume.api().store(candidate.id, objectToFormData({ resume }));
      }
    },
    addFile() {
      this.$refs.resumeInput.click();
    },
    handleFileUpload(event) {
      this.resumeError = null;
      this.resume = event.target.files[0];
      this.resumeName = this.resume.name;
      if (this.resume.size >= 5000000) {
        this.resumeError = this.$t(`${this.$t('sizeWarning')} 5 MB.`);
      }
    },
    clearResume() {
      this.resumeName = null;
      this.resume = null;
      this.resumeError = null;
      this.$refs.resumeInput.value = '';
    },
    getClearPhone(phone) {
      if (!phone) {
        return '';
      }

      return phone.replace(/^\D+/g, '');
    },
    async openWhatsApp(messageText) {
      const hasWhatsApp = await this.detectWhatsApp(this.getClearPhone(this.candidatePhone), messageText);
      window.removeEventListener('blur', this.blurEventListener);

      if (hasWhatsApp) {
        this.$refs.whatsAppApplicationLink.click();
      } else {
        this.$refs.whatsAppLink.click();
      }
    },
    detectWhatsApp(phone, text) {
      const uri = `whatsapp://send/?phone=${encodeURIComponent(phone)}&text=${encodeURIComponent(text)}`;

      const onIE = () => {
        return new Promise((resolve) => {
          window.navigator.msLaunchUri(
            uri,
            () => resolve(true),
            () => resolve(false),
          );
        });
      };

      const notOnIE = () => {
        return new Promise((resolve) => {
          const a = document.getElementById('wapp-launcher') || document.createElement('a');
          a.id = 'wapp-launcher';
          a.href = uri;
          a.style.display = 'none';
          document.body.appendChild(a);

          const start = Date.now();
          const timeoutToken = setTimeout(() => {
            if (Date.now() - start > 1250) {
              resolve(true);
            } else {
              resolve(false);
            }
          }, 1000);

          const handleBlur = () => {
            clearTimeout(timeoutToken);
            resolve(true);
          };
          this.blurEventListener = handleBlur;
          window.addEventListener('blur', handleBlur);

          a.click();
        });
      };

      return window.navigator.msLaunchUri ? onIE() : notOnIE();
    },
    setAdditionalInputs() {
      if (!this.vacancyCandidateAdditionalFields) {
        return;
      }
      const addFields = {};
      this.vacancyCandidateAdditionalFields.forEach((field) => {
        addFields[field.value] = null;
      });
      this.additionalFields = {
        ...addFields,
      };
    },
    setAdditionalValue(value, inputName) {
      if (this.additionalFields.hasOwnProperty(inputName)) {
        this.additionalFields[inputName] = value;
      }
    },
  },
};
</script>

<style lang="scss">
.tooltip-link {
  color: rgba(0, 0, 0, 0.6) !important;
  text-decoration: none;
}
.theme--light.v-radio--is-disabled .tooltip-link {
  color: rgba(0, 0, 0, 0.38) !important;
}
</style>

<style lang="scss" scoped>
  .notify {
    border: 1px solid #C2C2C2;
    border-radius: 0.25rem;
    padding: 0.812rem 0.9375rem 1px;
    position: relative;
    margin-bottom: 1.25rem;
    &__item {
      margin-bottom: 8px;
    }
    &__rest-hint {
      display: none;
      position: absolute;
      white-space: nowrap;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
      border-radius: 5px;
      top: 0;
      transform: translateY(calc(-100% - 15px))  translateX(-50%);
      left: 50%;
      background: #e0e0e0;
      padding: 5px 10px;

      &:after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        width: 15px;
        height: 15px;
        border-radius: 1px;
        transform: rotate(45deg) translate(-80%, 0%);
        background: inherit;
        z-index: -1;
      }
    }
    &__rest-amount {
      position: relative;
      margin-left: 5px;
      &:hover {
        .notify__rest-hint {
          display: block;
        }
      }
    }
  }
</style>

<i18n locale="en" lang="yaml">
  firstName: First name
  lastName: Last name
  phone: Phone number
  notifyLabel: Notify by
  notifyByEmail: Email
  notifyByEmailAndWhatsapp: Email + WhatsApp
  notifyBySMS: Email + SMS
  notifyByCall: Email + Call
  quotaLeft: ({count} left)
  quotaEmpty: (Balance is empty!)
  invite: Invite
  cancel: Cancel
  addCandidateTip: You can record an interview after adding a candidate
  chromeExt1: 'Inviting candidates is more convenient with {0}'
  chromeExt2: Google Chrome extension
  messageNotSent: Message not sent
  sendCandidateLink: The invitation has been sent!
  sendCandidateLinkText: 'We have already sent a notification to the candidate, but if you wish, you can personally send them an invitation link (for example, via a messenger)'
  inviteToVirtualInterview: Invite to a virtual interview
  resume: Resume
  sendWhatsappInviteButton: Send invitation via WhatsApp
  whatsAppInviteMessage: '{companyName} invites you for an interview. {url}'
  smsTooltip: '{count} SMS left | {count} SMS left | {count} SMS left'
  callTooltip: '{count} calls left | {count} call left | {count} calls left'
  howNotify: How do we notify candidates
</i18n>

<i18n locale="ru" lang="yaml">
  firstName: Имя
  lastName: Фамилия
  phone: Телефон
  notifyLabel: Уведомить по
  notifyByEmail: Email
  notifyByEmailAndWhatsapp: Email + WhatsApp
  notifyBySMS: Email + SMS
  notifyByCall: Email + Звонок роботом
  quotaLeft: ({count} осталось)
  quotaEmpty: (Пустая квота!)
  invite: Пригласить
  cancel: Отменить
  addCandidateTip: После добавления кандидата вы сможете записать очное интервью
  chromeExt1: 'Приглашать кандидатов удобнее с {0}'
  chromeExt2: расширением для Google Chrome
  messageNotSent: Не удалось отправить оповещение
  sendCandidateLink: Приглашение отправлено!
  sendCandidateLinkText: 'Мы уже отправили кандидату оповещение, но при желании вы можете лично выслать ему пригласительную ссылку (например, через мессенджер)'
  inviteToVirtualInterview: Пригласить на автоматическое интервью
  resume: Резюме
  sendWhatsappInviteButton: Отправить приглашение в WhatsApp
  whatsAppInviteMessage: '{companyName} приглашает вас пройти собеседование. {url}'
  smsTooltip: 'Осталось {count} SMS | Осталось {count} SMS | Осталось {count} SMS | Осталось {count} SMS'
  callTooltip: 'Осталось {count} звонков | Остался {count} звонок | Осталось {count} звонка | Осталось {count} звонков'
  howNotify: Как мы оповещаем кандидатов
</i18n>
